import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Coins, Loader2, Sparkles, Mail, Send, BellOff, Bell, ShieldCheck, ShieldAlert, RefreshCw, FileText, TrendingUp, Lock, Wallet, Trash2, Plus, CalendarClock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = [
  { code: "GBP", label: "£  GBP  ·  British Pound" },
  { code: "USD", label: "$  USD  ·  US Dollar" },
  { code: "EUR", label: "€  EUR  ·  Euro" },
  { code: "CAD", label: "C$ CAD  ·  Canadian Dollar" },
  { code: "AUD", label: "A$ AUD  ·  Australian Dollar" },
  { code: "CHF", label: "CHF     ·  Swiss Franc" },
  { code: "JPY", label: "¥  JPY  ·  Japanese Yen" },
  { code: "SGD", label: "S$ SGD  ·  Singapore Dollar" },
  { code: "INR", label: "₹  INR  ·  Indian Rupee" },
  { code: "BRL", label: "R$ BRL  ·  Brazilian Real" },
];

const SettingsDialog = ({ open, onOpenChange, settings, plan, onSaved, onManagePlan }) => {
  const [homeCurrency, setHomeCurrency] = useState(settings?.home_currency || "GBP");
  const [reminderEmail, setReminderEmail] = useState(settings?.reminder_email || "");
  const [remindersEnabled, setRemindersEnabled] = useState(
    settings?.reminders_enabled === undefined ? true : !!settings.reminders_enabled
  );
  const [spendingAlertsEnabled, setSpendingAlertsEnabled] = useState(
    settings?.spending_alerts_enabled === undefined ? true : !!settings.spending_alerts_enabled
  );
  const [spendingAlertsThreshold, setSpendingAlertsThreshold] = useState(
    settings?.spending_alerts_threshold || 30
  );
  const [sendingSpendingTest, setSendingSpendingTest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(
    !!settings?.weekly_digest_enabled
  );
  const [sendingDigestTest, setSendingDigestTest] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Category budgets
  const [budgets, setBudgets] = useState([]);
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [budgetsCurrency, setBudgetsCurrency] = useState("GBP");
  const [newBudgetCat, setNewBudgetCat] = useState("food");
  const [newBudgetAmt, setNewBudgetAmt] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);

  const fetchBudgets = async () => {
    setBudgetsLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/categories`, { withCredentials: true });
      setBudgets(res.data?.data || []);
      setBudgetsCurrency(res.data?.home_currency || "GBP");
    } catch (e) {
      setBudgets([]);
    } finally {
      setBudgetsLoading(false);
    }
  };

  const saveBudget = async () => {
    const amt = parseFloat(newBudgetAmt);
    if (!amt || amt <= 0) {
      toast.error("Enter a monthly limit greater than zero");
      return;
    }
    setSavingBudget(true);
    try {
      await axios.post(
        `${API}/budgets/categories`,
        { category: newBudgetCat, monthly_limit: amt },
        { withCredentials: true }
      );
      setNewBudgetAmt("");
      await fetchBudgets();
      toast.success("Budget saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error("Budgets are a Pro feature");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't save budget");
      }
    } finally {
      setSavingBudget(false);
    }
  };

  const deleteBudget = async (category) => {
    try {
      await axios.delete(`${API}/budgets/categories/${category}`, { withCredentials: true });
      await fetchBudgets();
    } catch {
      toast.error("Couldn't delete budget");
    }
  };

  const fetchReminderHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/reminders/history`, {
        params: { limit: 5 },
        withCredentials: true,
      });
      setReminderHistory(res.data?.entries || []);
    } catch {
      setReminderHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(
        `${API}/payments/portal`,
        { return_url: window.location.origin + "/dashboard" },
        { withCredentials: true }
      );
      const url = res.data?.portal_url;
      if (!url) throw new Error("No portal URL returned");
      window.location.assign(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "no_subscription") {
        toast.error(detail.message);
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Could not open Stripe portal"
        );
      }
      setOpeningPortal(false);
    }
  };

  const fetchDomainStatus = async (opts = { verify: false }) => {
    try {
      setCheckingDomain(true);
      const url = opts.verify
        ? `${API}/reminders/verify_sender_domain`
        : `${API}/reminders/sender_domain_status`;
      const res = opts.verify ? await axios.post(url) : await axios.get(url);
      setDomainStatus(res.data || null);
      if (opts.verify) {
        toast.success("Re-check triggered · give Resend a moment");
      }
    } catch (err) {
      // Non-blocking — just clear state
      if (opts.verify) {
        toast.error("Could not re-check domain");
      }
    } finally {
      setCheckingDomain(false);
    }
  };

  useEffect(() => {
    if (open) {
      setHomeCurrency(settings?.home_currency || "GBP");
      setReminderEmail(settings?.reminder_email || "");
      setRemindersEnabled(
        settings?.reminders_enabled === undefined ? true : !!settings.reminders_enabled
      );
      setSpendingAlertsEnabled(
        settings?.spending_alerts_enabled === undefined ? true : !!settings.spending_alerts_enabled
      );
      setSpendingAlertsThreshold(settings?.spending_alerts_threshold || 30);
      setWeeklyDigestEnabled(!!settings?.weekly_digest_enabled);
      fetchDomainStatus({ verify: false });
      fetchReminderHistory();
      fetchBudgets();
    }
  }, [open, settings]);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reminderEmail);
  const isDirty =
    homeCurrency !== settings?.home_currency ||
    reminderEmail !== (settings?.reminder_email || "") ||
    remindersEnabled !== (settings?.reminders_enabled === undefined ? true : !!settings.reminders_enabled) ||
    spendingAlertsEnabled !== (settings?.spending_alerts_enabled === undefined ? true : !!settings.spending_alerts_enabled) ||
    Number(spendingAlertsThreshold) !== Number(settings?.spending_alerts_threshold || 30) ||
    weeklyDigestEnabled !== !!settings?.weekly_digest_enabled;

  const save = async () => {
    if (reminderEmail && !emailIsValid) {
      toast.error("Reminder email doesn't look valid");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/settings`,
        {
          home_currency: homeCurrency,
          reminder_email: reminderEmail || null,
          reminders_enabled: remindersEnabled,
          spending_alerts_enabled: spendingAlertsEnabled,
          spending_alerts_threshold: Number(spendingAlertsThreshold),
          weekly_digest_enabled: weeklyDigestEnabled,
        },
        { withCredentials: true }
      );
      toast.success("Preferences saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail[0]?.msg || "Could not save your settings"
        : "Could not save your settings";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const sendTestNow = async () => {    if (!reminderEmail || !emailIsValid) {
      toast.error("Add a valid reminder email first");
      return;
    }
    setSendingTest(true);
    try {
      // If the email or enabled state has changed but not saved, persist first.
      if (isDirty) {
        await axios.put(
          `${API}/user/settings`,
          {
            home_currency: homeCurrency,
            reminder_email: reminderEmail || null,
            reminders_enabled: remindersEnabled,
            spending_alerts_enabled: spendingAlertsEnabled,
            spending_alerts_threshold: Number(spendingAlertsThreshold),
            weekly_digest_enabled: weeklyDigestEnabled,
          },
          { withCredentials: true }
        );
        onSaved && onSaved();
      }
      const res = await axios.post(
        `${API}/reminders/send_now`,
        {},
        { withCredentials: true }
      );
      const d = res.data || {};
      // Refresh the parent so settings-last-sent picks up the new timestamp
      onSaved && onSaved();
      if (d.sent) {
        toast.success(
          d.cards_included
            ? `Test reminder sent to ${d.to} for ${d.cards_included} card${d.cards_included === 1 ? "" : "s"}`
            : `Test reminder sent to ${d.to} (no cards currently due)`
        );
      } else {
        toast.info(`Reminder not sent · ${d.reason || "no cards due"}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const sendSpendingAlertTest = async () => {
    setSendingSpendingTest(true);
    try {
      if (isDirty) {
        await axios.put(
          `${API}/user/settings`,
          {
            home_currency: homeCurrency,
            reminder_email: reminderEmail || null,
            reminders_enabled: remindersEnabled,
            spending_alerts_enabled: spendingAlertsEnabled,
            spending_alerts_threshold: Number(spendingAlertsThreshold),
            weekly_digest_enabled: weeklyDigestEnabled,
          },
          { withCredentials: true }
        );
        onSaved && onSaved();
      }
      const res = await axios.post(
        `${API}/user/spending_alerts_test`,
        {},
        { withCredentials: true }
      );
      const d = res.data || {};
      onSaved && onSaved();
      if (d.sent) {
        toast.success(
          `Alert sent to ${d.to} · ${d.cards_alerted} card${d.cards_alerted === 1 ? "" : "s"} over ${d.threshold}%`
        );
      } else {
        toast.info(`Alert not sent · ${d.reason || "no cards over threshold"}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error("Spending Alerts is a Pro feature — upgrade to enable.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send test alert");
      }
    } finally {
      setSendingSpendingTest(false);
    }
  };

  const sendWeeklyDigestTest = async () => {
    if (!reminderEmail || !emailIsValid) {
      toast.error("Add a valid reminder email first");
      return;
    }
    setSendingDigestTest(true);
    try {
      if (isDirty) {
        await axios.put(
          `${API}/user/settings`,
          {
            home_currency: homeCurrency,
            reminder_email: reminderEmail || null,
            reminders_enabled: remindersEnabled,
            spending_alerts_enabled: spendingAlertsEnabled,
            spending_alerts_threshold: Number(spendingAlertsThreshold),
            weekly_digest_enabled: weeklyDigestEnabled,
          },
          { withCredentials: true }
        );
        onSaved && onSaved();
      }
      const res = await axios.post(
        `${API}/reminders/send_weekly_digest_now`,
        {},
        { withCredentials: true }
      );
      const d = res.data || {};
      // Refresh parent so the "last sent" row updates even if nothing was dirty.
      onSaved && onSaved();
      if (d.sent) {
        const paid = Number(d.total_paid_home || 0).toFixed(2);
        toast.success(
          `Weekly digest sent to ${d.to} · ${homeCurrency} ${paid} paid, ${d.due_count || 0} due, ${d.alert_count || 0} budget alert${d.alert_count === 1 ? "" : "s"}`
        );
      } else {
        toast.info(`Digest not sent · ${d.reason || "unknown"}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send test digest");
    } finally {
      setSendingDigestTest(false);
    }
  };

  const isPro = plan?.name === "pro";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E172C] border border-[#1A2849] text-white sm:max-w-lg rounded-2xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto"
        data-testid="settings-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
              <Coins size={15} className="text-[#00E676] sm:hidden" />
              <Coins size={17} className="text-[#00E676] hidden sm:block" />
            </div>
            <div className="min-w-0">
              <DialogTitle
                className="text-white text-base sm:text-xl tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Preferences
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-xs sm:text-sm">
                Currency, reminders, and plan.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-3 sm:space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="home-currency"
              className="block text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold"
            >
              Home currency
            </label>
            <select
              id="home-currency"
              value={homeCurrency}
              onChange={(e) => setHomeCurrency(e.target.value)}
              className="w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm text-white focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20 transition-colors"
              data-testid="settings-home-currency-select"
              style={{ fontFamily: "'Manrope', monospace" }}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code} className="bg-[#060B19]">
                  {c.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-300">
              New manual cards and dashboard totals will default to this currency.
            </p>
          </div>

          {/* Reminders */}
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="settings-reminders-section">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold flex items-center gap-2">
                  <Mail size={12} className="text-[#00E676]" /> Payment reminders
                </div>
                <p className="text-xs text-slate-300 mt-1">
                  Emailed 2 days before each due date · 09:00 UTC daily.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRemindersEnabled((v) => !v)}
                data-testid="settings-reminders-toggle"
                aria-label={remindersEnabled ? "Turn reminders off" : "Turn reminders on"}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                  remindersEnabled
                    ? "border-[#00E676]/40 bg-[#00E676]/[0.08] text-[#00E676]"
                    : "border-white/10 bg-white/[0.02] text-slate-300 hover:text-white"
                }`}
              >
                {remindersEnabled ? <Bell size={11} /> : <BellOff size={11} />}
                {remindersEnabled ? "On" : "Off"}
              </button>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="reminder-email"
                className="block text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold"
              >
                Reminder email
              </label>
              <input
                id="reminder-email"
                type="email"
                value={reminderEmail}
                onChange={(e) => setReminderEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20 transition-colors"
                data-testid="settings-reminder-email-input"
              />
              {settings?.last_reminder_sent_at && (
                <p className="text-[11px] text-slate-300" data-testid="settings-last-sent">
                  Last sent · {new Date(settings.last_reminder_sent_at).toLocaleString()}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={sendTestNow}
              disabled={sendingTest || !emailIsValid}
              data-testid="settings-send-test-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] px-4 py-2 text-xs text-[#00E676] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sendingTest ? "Sending…" : "Send test email now"}
            </button>

            {domainStatus?.configured && domainStatus?.domain ? (
              <div
                className="rounded-lg border border-white/5 bg-black/20 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5"
                data-testid="settings-sender-domain-row"
              >
                <div className="min-w-0 flex items-center gap-2.5">
                  {domainStatus.status === "verified" ? (
                    <ShieldCheck size={14} className="text-[#00E676] flex-shrink-0" />
                  ) : (
                    <ShieldAlert size={14} className="text-amber-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
                      Sender domain
                    </div>
                    <div
                      className="text-xs text-white truncate"
                      style={{ fontFamily: "'Manrope', monospace" }}
                      data-testid="settings-sender-domain-value"
                    >
                      {domainStatus.sender}
                      <span
                        className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                          domainStatus.status === "verified"
                            ? "bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30"
                            : "bg-amber-400/10 text-amber-300 border border-amber-400/30"
                        }`}
                        data-testid="settings-sender-domain-status"
                      >
                        {domainStatus.status === "verified"
                          ? "Verified"
                          : domainStatus.status === "not_added"
                          ? "Not added"
                          : "Propagating"}
                      </span>
                    </div>
                    {domainStatus.status !== "verified" && (
                      <p className="text-[11px] text-slate-300 mt-0.5">
                        SPF verified. DKIM waiting on DNS cache — emails fall back to Resend's sender until then.
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => fetchDomainStatus({ verify: true })}
                  disabled={checkingDomain}
                  data-testid="settings-recheck-domain"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 hover:border-white/25 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-1.5 text-[11px] text-slate-300 hover:text-white transition-colors disabled:opacity-50 flex-shrink-0"
                  title="Ask Resend to re-check DNS"
                >
                  <RefreshCw size={11} className={checkingDomain ? "animate-spin" : ""} />
                  Re-check
                </button>
              </div>
            ) : null}
          </div>

          {/* Weekly Digest — Sundays 09:00 UTC — independent opt-in */}
          <div
            className="rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4 space-y-3 sm:space-y-4"
            data-testid="settings-weekly-digest-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold flex items-center gap-2">
                  <CalendarClock size={12} className="text-[#00E676]" /> Weekly digest
                </div>
                <p className="text-xs text-slate-300 mt-1">
                  One Sunday email · due cards for the week ahead, budget nudges, and total paid.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setWeeklyDigestEnabled((v) => !v)}
                data-testid="settings-weekly-digest-toggle"
                aria-label={weeklyDigestEnabled ? "Turn weekly digest off" : "Turn weekly digest on"}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                  weeklyDigestEnabled
                    ? "border-[#00E676]/40 bg-[#00E676]/[0.08] text-[#00E676]"
                    : "border-white/10 bg-white/[0.02] text-slate-300 hover:text-white"
                }`}
              >
                {weeklyDigestEnabled ? <Bell size={11} /> : <BellOff size={11} />}
                {weeklyDigestEnabled ? "On" : "Off"}
              </button>
            </div>

            {settings?.last_weekly_digest_sent_at && (
              <p className="text-[11px] text-slate-300" data-testid="settings-weekly-digest-last-sent">
                Last sent · {new Date(settings.last_weekly_digest_sent_at).toLocaleString()}
              </p>
            )}

            <button
              type="button"
              onClick={sendWeeklyDigestTest}
              disabled={sendingDigestTest || !emailIsValid}
              data-testid="settings-weekly-digest-send-test"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] px-4 py-2 text-xs text-[#00E676] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingDigestTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sendingDigestTest ? "Sending…" : "Send test digest now"}
            </button>
          </div>

          {/* Category Budgets (Pro) */}
          <div
            className="rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4 space-y-3"
            data-testid="settings-budgets-section"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold flex items-center gap-2">
                  <Wallet size={12} className="text-[#00E676]" /> Category Budgets
                  {!isPro && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] px-1.5 py-0.5 text-[9px] text-[#00E676] font-bold uppercase tracking-[0.18em]">
                      <Lock size={9} /> Pro
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-300 mt-1">
                  Cap a category (e.g. Food {budgetsCurrency} 150/mo). Email nudge at 80% and 100% of the cap · totals every card in {budgetsCurrency}.
                </p>
              </div>
            </div>

            {!isPro ? (
              <div
                className="rounded-lg border border-dashed border-[#00E676]/30 bg-[#00E676]/[0.04] px-3 py-3 text-center"
                data-testid="settings-budgets-upsell"
              >
                <p className="text-sm text-slate-200">
                  <Lock size={12} className="inline -mt-0.5 mr-1 text-[#00E676]" />
                  Category Budgets are Pro-only.
                </p>
                <button
                  type="button"
                  onClick={onManagePlan}
                  data-testid="settings-budgets-upgrade"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-4 py-1.5 text-xs transition-colors"
                >
                  <Sparkles size={12} /> Upgrade to Pro
                </button>
              </div>
            ) : (
              <>
                {budgetsLoading ? (
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> Loading…
                  </div>
                ) : budgets.length === 0 ? (
                  <p className="text-xs text-slate-400" data-testid="settings-budgets-empty">
                    No budgets yet. Add one below to start tracking.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="settings-budgets-list">
                    {budgets.map((b) => {
                      const over = b.pct >= 100;
                      const atRisk = b.pct >= 80 && !over;
                      const barColor = over ? "#FF5252" : atRisk ? "#F5B841" : "#00E676";
                      return (
                        <li
                          key={b.category}
                          className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5"
                          data-testid={`settings-budget-item-${b.category}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-white font-semibold truncate">
                                {b.label}
                              </div>
                              <div className="text-[11px] text-slate-400 mt-0.5">
                                {b.currency} {b.spent.toFixed(2)} of {b.currency} {b.monthly_limit.toFixed(2)}
                                {" · "}
                                <span style={{ color: barColor }}>{b.pct.toFixed(0)}%</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => deleteBudget(b.category)}
                              data-testid={`settings-budget-delete-${b.category}`}
                              aria-label={`Remove ${b.label} budget`}
                              className="text-slate-500 hover:text-[#FF5252] transition-colors p-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-[width] duration-500"
                              style={{ width: `${Math.min(100, Math.max(2, b.pct))}%`, background: barColor }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Add / update form */}
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 pt-1">
                  <div className="relative">
                    <select
                      value={newBudgetCat}
                      onChange={(e) => setNewBudgetCat(e.target.value)}
                      data-testid="settings-budget-new-category"
                      className="w-full appearance-none pr-7 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E676]/40"
                    >
                      <option value="transport">Transport</option>
                      <option value="food">Eating Out</option>
                      <option value="groceries">Groceries</option>
                      <option value="utilities">Bills</option>
                      <option value="other">Others</option>
                    </select>
                  </div>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={newBudgetAmt}
                    onChange={(e) => setNewBudgetAmt(e.target.value)}
                    placeholder={`e.g. 150.00`}
                    data-testid="settings-budget-new-amount"
                    className="rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E676]/40"
                  />
                  <button
                    type="button"
                    onClick={saveBudget}
                    disabled={savingBudget || !newBudgetAmt}
                    data-testid="settings-budget-save"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-3 py-2 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingBudget ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                    {savingBudget ? "Saving" : "Save"}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Adding a budget for a category that already exists updates the cap.
                </p>
              </>
            )}
          </div>

          {/* Spending Alerts (Pro) */}
          <div
            className="rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4 space-y-3 sm:space-y-4"
            data-testid="settings-spending-alerts-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold flex items-center gap-2">
                  <TrendingUp size={12} className="text-[#EF4444]" /> Spending Alerts
                  {!isPro && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] px-1.5 py-0.5 text-[9px] text-[#00E676] font-bold uppercase tracking-[0.18em]">
                      <Lock size={9} /> Pro
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-300 mt-1">
                  Daily email when any card crosses your utilization threshold · 07:00 UTC daily.
                </p>
              </div>
              <button
                type="button"
                onClick={() => isPro && setSpendingAlertsEnabled((v) => !v)}
                disabled={!isPro}
                data-testid="settings-spending-alerts-toggle"
                aria-label={spendingAlertsEnabled ? "Turn spending alerts off" : "Turn spending alerts on"}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                  spendingAlertsEnabled && isPro
                    ? "border-[#00E676]/40 bg-[#00E676]/[0.08] text-[#00E676]"
                    : "border-white/10 bg-white/[0.02] text-slate-300 hover:text-white disabled:cursor-not-allowed"
                }`}
              >
                {spendingAlertsEnabled ? <Bell size={11} /> : <BellOff size={11} />}
                {spendingAlertsEnabled ? "On" : "Off"}
              </button>
            </div>

            <div className={`space-y-2 ${!isPro ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor="spending-alerts-threshold"
                  className="block text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold"
                >
                  Utilization threshold
                </label>
                <div
                  className="text-2xl font-bold text-[#00E676]"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="settings-spending-alerts-threshold-value"
                >
                  {spendingAlertsThreshold}%
                </div>
              </div>
              <input
                id="spending-alerts-threshold"
                type="range"
                min="10"
                max="90"
                step="5"
                value={spendingAlertsThreshold}
                onChange={(e) => setSpendingAlertsThreshold(Number(e.target.value))}
                data-testid="settings-spending-alerts-threshold-slider"
                className="w-full accent-[#00E676]"
                disabled={!isPro}
              />
              <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium">
                <span>10% · very cautious</span>
                <span className="text-[#00E676]">30% · recommended</span>
                <span>90%</span>
              </div>
            </div>

            <button
              type="button"
              onClick={sendSpendingAlertTest}
              disabled={sendingSpendingTest || !isPro || !spendingAlertsEnabled}
              data-testid="settings-send-spending-alert-test"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] px-4 py-2 text-xs text-[#00E676] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingSpendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sendingSpendingTest ? "Sending…" : "Send test alert now"}
            </button>

            {settings?.last_spending_alert_at && (
              <p className="text-[11px] text-slate-300" data-testid="settings-last-spending-alert">
                Last alert · {new Date(settings.last_spending_alert_at).toLocaleString()}
              </p>
            )}

            {isPro && settings?.streak_days > 0 && (
              <div
                className="rounded-lg border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3 py-2.5 flex items-center gap-2.5"
                data-testid="settings-streak-badge"
              >
                <Sparkles size={13} className="text-[#00E676] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
                    Current streak
                  </div>
                  <div className="text-sm text-white font-semibold">
                    <span
                      className="text-[#00E676]"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {settings.streak_days}
                    </span>{" "}
                    day{settings.streak_days === 1 ? "" : "s"} under {settings.spending_alerts_threshold}%
                    {settings.last_celebrated_streak > 0 && (
                      <span className="text-slate-300 font-normal">
                        {" · "}last milestone {settings.last_celebrated_streak}d
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Reminder history — last 5 email events across all channels */}
            <div className="mt-1 pt-3 border-t border-white/5" data-testid="settings-reminder-history">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
                  Recent activity
                </div>
                <button
                  type="button"
                  onClick={fetchReminderHistory}
                  disabled={historyLoading}
                  aria-label="Refresh history"
                  className="text-slate-400 hover:text-white transition-colors disabled:opacity-40"
                  data-testid="settings-reminder-history-refresh"
                >
                  <RefreshCw size={11} className={historyLoading ? "animate-spin" : ""} />
                </button>
              </div>
              {reminderHistory.length === 0 ? (
                <p className="text-xs text-slate-400" data-testid="settings-reminder-history-empty">
                  {historyLoading ? "Loading…" : "No reminder emails yet — they'll appear here after we send you your first alert."}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {reminderHistory.map((e, i) => {
                    const kindMeta = {
                      due_soon: { icon: <Bell size={11} />, color: "text-sky-300", label: "Due soon" },
                      spending_alert: { icon: <ShieldAlert size={11} />, color: "text-amber-300", label: "Spending alert" },
                      streak: { icon: <Sparkles size={11} />, color: "text-[#00E676]", label: "Streak" },
                    }[e.kind] || { icon: <Mail size={11} />, color: "text-slate-300", label: "Email" };
                    let when = "";
                    try {
                      when = new Date(e.sent_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    } catch {
                      when = e.sent_at || "";
                    }
                    return (
                      <li
                        key={`${e.resend_id || e.sent_at}-${i}`}
                        className="flex items-start gap-2 text-xs"
                        data-testid={`reminder-history-entry-${i}`}
                      >
                        <span className={`mt-0.5 flex-shrink-0 ${kindMeta.color}`}>{kindMeta.icon}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-white truncate">{e.detail}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            <span className={kindMeta.color}>{kindMeta.label}</span>
                            {" · "}
                            {when}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
                  Plan
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="settings-plan-name"
                  >
                    {isPro ? (
                      <>
                        <Sparkles size={13} className="text-[#00E676]" /> CardWatch Pro
                      </>
                    ) : (
                      <>CardWatch Free</>
                    )}
                  </span>
                  {plan?.card_limit ? (
                    <span className="text-xs text-slate-300">
                      · {plan?.counts?.total ?? 0} of {plan.card_limit} cards
                    </span>
                  ) : (
                    <span className="text-xs text-slate-300">· Unlimited cards</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={isPro ? handleOpenPortal : onManagePlan}
                disabled={openingPortal}
                data-testid="settings-manage-plan-button"
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors whitespace-nowrap disabled:opacity-60 ${
                  isPro
                    ? "border-white/10 hover:border-white/30 bg-white/[0.02] text-slate-300 hover:text-white"
                    : "border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] text-[#00E676]"
                }`}
              >
                {openingPortal && <Loader2 size={12} className="animate-spin" />}
                {isPro
                  ? (openingPortal ? "Opening…" : "Manage or cancel")
                  : "Upgrade to Pro"}
              </button>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1 text-[11px] text-slate-300"
            data-testid="settings-legal-row"
          >
            <div className="flex items-center gap-4">
              <Link
                to="/privacy"
                onClick={() => onOpenChange(false)}
                data-testid="settings-privacy-link"
                className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <FileText size={11} className="text-[#00E676]/80" />
                Privacy Policy
              </Link>
              <a
                href="mailto:privacy@cardwatchapp.com"
                data-testid="settings-contact-link"
                className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <Mail size={11} className="text-[#00E676]/80" />
                Contact
              </a>
            </div>
            <span className="text-slate-600 text-[10px] sm:text-[11px]">CardWatch · v1.0.0</span>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-full border border-white/10 hover:border-white/30 px-5 py-2.5 text-xs text-slate-300 hover:text-white transition-colors"
            data-testid="settings-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !isDirty}
            data-testid="settings-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-6 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
